import { type FC, useState } from 'react';
import { Box, Flex, useBreakpointValue } from '@chakra-ui/react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { Button, Modal, Text } from '../../../ui-components';
import { maxLength } from '../../../utils/validation';
import { Field } from '../../field';
import type { FieldConfig } from '../../field';
import type { Video } from '../types';

export interface VideoModalProps {
    isOpen: boolean;
    onClose: () => void;
    productName: string | undefined;
    productImage: string | undefined;
    field: FieldConfig;
    onSubmit: (value: Video) => void;
    form: UseFormReturn<
        {
            rating: number;
            review: string;
            reviewTitle: string;
            firstName: string;
            lastName: string;
            email: string;
            nickName: string;
            video: string;
            photo: string;
        },
        unknown,
        undefined
    >;
}
export const VideoModal: FC<VideoModalProps> = ({
    isOpen,
    onClose,
    productName,
    productImage,
    field,
    onSubmit,
    form,
}) => {
    const intl = useIntl();
    const [step, setStep] = useState<number>(1);
    const buttonWidth = useBreakpointValue<'wide' | 'normal'>({ base: 'wide', sm: 'normal' });
    const captionForm = useForm({ reValidateMode: 'onChange', mode: 'onChange' });

    const preSubmit = (): void => {
        const videoData: Video = {
            id: uuidv4(),
            caption: captionForm.getValues()['video-caption'] as string,
            type: 'video',
            url: form.getValues('video'),
            thumbnailUrl: getThumbnailUrl(),
        };

        onSubmit(videoData);
        resetModal();
    };

    const resetModal = (): void => {
        captionForm.reset();
        form.setValue('video', '');
        onCloseModal();
    };

    const proceedToNextStep = async (): Promise<void> => {
        await form.trigger('video');
        const errorsLength = Object.keys(form.formState.errors).some(
            (errorName) => errorName === 'video',
        );
        if (errorsLength) {
            return;
        }

        setStep(2);
    };

    const onCloseModal = (): void => {
        setStep(1);
        onClose();
    };

    const getThumbnailUrl = (): string =>
        `https://img.youtube.com/vi/${extractVideoId(form.getValues('video'))}/default.jpg`;

    const extractVideoId = (url: string): string | null => {
        const videoIdIndex = url.indexOf('v=');

        if (videoIdIndex !== -1) {
            const ampersandIndex = url.indexOf('&', videoIdIndex);
            const videoId =
                ampersandIndex !== -1
                    ? url.substring(videoIdIndex + 2, ampersandIndex)
                    : url.substring(videoIdIndex + 2);

            return videoId;
        }

        return null;
    };

    return (
        <Modal
            title={
                step === 1
                    ? intl.formatMessage({
                          id: 'turn-to.video-modal.add-video',
                          defaultMessage: 'Add your video',
                      })
                    : intl.formatMessage({
                          id: 'turn-to.video-modal.add-caption',
                          defaultMessage: 'Add caption',
                      })
            }
            id="video-modal"
            isOpen={isOpen}
            onClose={onCloseModal}
            variant="turn-to"
        >
            {step === 1 && (
                <>
                    <Flex flexWrap={{ base: 'wrap', sm: 'nowrap' }} gap={4} marginBottom={4}>
                        <Box width={40} height={40}>
                            <img
                                width="100%"
                                height="100%"
                                src={productImage}
                                alt={productName || 'Product'}
                            />
                        </Box>
                        <Box width="100%" flex={{ base: 'none', sm: 1 }}>
                            <Field {...field} variant="new-style" />
                            <Flex marginTop={4} gap={2}>
                                <Button
                                    variant="primary-black"
                                    onClick={proceedToNextStep}
                                    wide={buttonWidth === 'wide'}
                                >
                                    <FormattedMessage
                                        id="turn-to.video-modal.add"
                                        defaultMessage="Add"
                                    />
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={onCloseModal}
                                    wide={buttonWidth === 'wide'}
                                >
                                    <FormattedMessage
                                        id="turn-to.video-modal.back"
                                        defaultMessage="Back"
                                    />
                                </Button>
                            </Flex>
                        </Box>
                    </Flex>

                    <Box
                        borderBottomWidth="1px"
                        borderColor="slt-pepper.300"
                        paddingBottom={6}
                        marginBottom={2}
                    >
                        <Text style="bold" size="small">
                            {productName}
                        </Text>
                    </Box>
                    <Text size="small">
                        <FormattedMessage
                            id="turn-to.review-modal.submission-description"
                            defaultMessage="Your submission is subject to our terms of use. Do not link or upload copyrighted material without the copyright owner’s permission."
                        />
                    </Text>
                </>
            )}
            {step === 2 && (
                <Box>
                    <Flex
                        flexWrap={{ base: 'wrap', sm: 'nowrap' }}
                        gap={4}
                        borderBottomWidth="1px"
                        borderColor="slt-pepper.300"
                        paddingBottom={6}
                        marginBottom={2}
                    >
                        <Box width={40} height={40}>
                            <img
                                src={getThumbnailUrl()}
                                alt="uploaded pic"
                                width="100%"
                                height="100%"
                            />
                        </Box>
                        <Box flex={1}>
                            <Field
                                type="textarea"
                                name="video-caption"
                                control={captionForm.control}
                                label={intl.formatMessage({
                                    defaultMessage: 'Caption (optional)',
                                    id: 'turn-to.photo-modal.caption',
                                })}
                                helpText={
                                    <Text size="small">
                                        <FormattedMessage
                                            id="turn-to.photo-modal.caption-helper"
                                            defaultMessage="Please describe this media in 100 characters or less"
                                        />
                                    </Text>
                                }
                                error={captionForm.formState.errors['video-caption']}
                                rules={{
                                    maxLength: {
                                        value: maxLength.caption,
                                        message: intl.formatMessage(
                                            {
                                                defaultMessage: 'No more than {max} characters',
                                                id: 'error.field.maxLength',
                                            },
                                            {
                                                max: maxLength.caption,
                                            },
                                        ),
                                    },
                                }}
                            />
                            <Flex gap={2} marginTop={4}>
                                <Button variant="primary-black" onClick={preSubmit}>
                                    <FormattedMessage
                                        id="turn-to.video-modal.next"
                                        defaultMessage="Next"
                                    />
                                </Button>
                                <Button variant="secondary" onClick={resetModal}>
                                    <FormattedMessage
                                        id="turn-to.video-modal.cancel"
                                        defaultMessage="Cancel"
                                    />
                                </Button>
                            </Flex>
                        </Box>
                    </Flex>
                    <Text size="small">
                        <FormattedMessage
                            id="turn-to.photo-modal.next-description"
                            defaultMessage='By choosing "Next" your visuals will be submitted to our product gallery, even if you decide not to submit your review.'
                        />
                    </Text>
                    <Text size="small">
                        <FormattedMessage
                            id="turn-to.photo-modal.submission-description"
                            defaultMessage="Your submission is subject to our terms of use. Do not link or upload copyrighted material without the copyright owner’s permission."
                        />
                    </Text>
                </Box>
            )}
        </Modal>
    );
};
